import {
  customFieldsListOptions,
  EntityTypeEnum,
  leadsCreateMutation,
  leadsListQueryKey,
  leadsPartialUpdateMutation,
  leadsPipelineListQueryKey,
  leadsRetrieveOptions,
  leadsRetrieveQueryKey,
  leadsTypesListOptions,
  ReturnLeadReadable
} from '@crm/api'
import {
  GenericDetails,
  Lead,
  LeadDetails
} from '@crm/features/leads/components'
import { useStyles } from '@crm/features/leads/styles'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { paths } from '@crm/routes'
import {
  defaultNonPagination,
  getSelectIdValue,
  getUserFullName,
  isDuplicateError,
  transformCustomFieldValues,
  transformCustomFieldValuesToForm,
  withValues
} from '@crm/utils'
import { useInvalidatePredicateQueries, useSessionContext } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import {
  DEFAULT_ISO_DATE_FORMAT,
  displayDate,
  format,
  getStepForFieldError,
  showApiErrors
} from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Card, Flex, Form, notification, Steps } from 'antd'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'

import { useParams } from 'react-router-dom'

import { FIELD_STEP_MAPPING } from './constants'

type Props = {
  leadDetails?: ReturnLeadReadable
  onCancelModal?: () => void
}

export const LeadCreate = ({ leadDetails, onCancelModal }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const { user } = useSessionContext()
  const { id } = useParams()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const navigate = useAppNavigate()

  const [form] = Form.useForm()
  const [currentStep, setCurrentStep] = useState(0)

  const phoneNumbers = Form.useWatch(['contact', 'phone_numbers'], form)

  const { data: lead, isLoading } = useQuery({
    ...leadsRetrieveOptions({ path: { id: Number(id) } }),
    enabled: !!id
  })

  const { data: customFieldsLead } = useQuery({
    ...customFieldsListOptions({
      query: {
        entity_type: [EntityTypeEnum.LEAD],
        ...defaultNonPagination
      }
    })
  })

  const { data: customFieldsContact } = useQuery({
    ...customFieldsListOptions({
      query: {
        entity_type: [EntityTypeEnum.CONTACT],
        ...defaultNonPagination
      }
    })
  })

  const { data: leadsTypesData } = useQuery({
    ...leadsTypesListOptions({
      query: { page: 1, per_page: 1, ordering: '-is_default' }
    })
  })

  const handlerMutate = {
    onSuccess: async (lead: ReturnLeadReadable) => {
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })

      await invalidateQueries([
        leadsListQueryKey(),
        leadsRetrieveQueryKey({ path: { id: Number(lead?.id) } }),
        leadsPipelineListQueryKey()
      ])

      if (onCancelModal) {
        onCancelModal()
      } else {
        navigate(paths.leadsDetails.root(String(lead?.id)))
      }
    },
    onError: (e: AxiosError) => {
      if (isDuplicateError(e)) {
        setCurrentStep(2)
        return
      }

      const errorStep = getStepForFieldError(e, FIELD_STEP_MAPPING)
      if (errorStep !== null) {
        setCurrentStep(errorStep)
      }

      requestAnimationFrame(() => showApiErrors(e, form))
    }
  }

  const {
    mutate: addMutate,
    isPending: isAddPending,
    error: addError,
    reset: resetAdd
  } = useMutation({
    ...leadsCreateMutation(),
    ...handlerMutate
  })

  const {
    mutate: editMutate,
    isPending: isEditPending,
    error: editError,
    reset: resetEdit
  } = useMutation({
    ...leadsPartialUpdateMutation(),
    ...handlerMutate
  })

  // Clear the duplicate-lead alert once the user edits any phone number
  useEffect(() => {
    resetAdd()
    resetEdit()
  }, [phoneNumbers, resetAdd, resetEdit])

  const prevStep = () => setCurrentStep((prev) => prev - 1)
  const nextStep = async () => {
    await form.validateFields()
    setCurrentStep((prev) => prev + 1)
  }

  const leadData = lead ?? leadDetails

  const onSubmit = async () => {
    await form.validateFields()
    const values = form.getFieldsValue(true)

    const {
      owner,
      industry,
      contact,
      source,
      country,
      reason,
      size,
      status,
      sub_industry,
      demo_date,
      lead_type_code: _lead_type_code,
      ...rest
    } = values

    const contactLeads = leadData
      ? contact?.leads
      : contact?.leads?.map((lead?: ReturnLeadReadable) => lead?.id)

    const transformedCustomFieldsLead = customFieldsLead?.results
      ? transformCustomFieldValues(
          values?.custom_field_values,
          customFieldsLead.results
        )
      : []

    const transformedCustomFieldsContact = customFieldsContact?.results
      ? transformCustomFieldValues(
          values?.contact?.custom_field_values,
          customFieldsContact.results
        )
      : []

    const data = withValues({
      ...rest,
      contact: {
        ...contact,
        custom_field_values: transformedCustomFieldsContact,
        leads: contactLeads
      },
      owner: getSelectIdValue(
        owner,
        leadData ? leadData?.owner?.id : user?.user?.sso_id ?? undefined
      ),
      industry: getSelectIdValue(industry, leadData?.industry?.id),
      source: getSelectIdValue(source, leadData?.source?.id),
      country: getSelectIdValue(country, leadData?.country?.id),
      reason: getSelectIdValue(reason, leadData?.reason?.id),
      size: getSelectIdValue(size, leadData?.size?.id),
      status: getSelectIdValue(status, leadData?.status?.id),
      sub_industry: getSelectIdValue(sub_industry, leadData?.sub_industry?.id),
      demo_date: demo_date && format(demo_date, DEFAULT_ISO_DATE_FORMAT),
      custom_field_values: transformedCustomFieldsLead
    })

    if (leadData) {
      editMutate({ path: { id: leadData?.id ?? Number(id) }, body: data })
    } else {
      addMutate({ body: data })
    }
  }

  const currentComponent = [
    {
      title: t('contacts.lead'),
      child: (
        <Lead
          form={form}
          isEdit={!!leadData}
          hasIdnp={!!leadData?.identification_number}
        />
      )
    },
    {
      title: t('contacts.contact'),
      child: <GenericDetails form={form} isEdit={!!leadData} />
    },
    {
      title: t('lead.lead_details'),
      child: (
        <LeadDetails
          form={form}
          isEdit={!!leadData}
          duplicateError={addError ?? editError}
          onCloseDuplicate={() => {
            resetAdd()
            resetEdit()
          }}
        />
      )
    }
  ]

  const onCancel = () => {
    if (isFirstPage) {
      return id
        ? navigate(paths.leadsDetails.root(String(id)))
        : navigate(paths.leads)
    }

    return prevStep()
  }

  const isFirstPage = currentStep === 0
  const isLastPage = currentComponent.length === currentStep + 1
  const isPending = isAddPending || isEditPending

  useEffect(() => {
    const typesResults = leadsTypesData?.results?.[0]
    const defaultTypes = typesResults?.is_default ? typesResults : undefined

    if (user && leadData) {
      const formData = {
        ...(leadData && {
          ...leadData,
          owner: leadData?.owner?.id,
          lead_type: defaultTypes?.id ?? leadData?.status?.lead_type?.id,
          lead_type_code: leadData?.status?.lead_type?.code,
          tags: leadData?.tags?.map((tag) => tag?.id),
          source: leadData?.source && {
            value: leadData.source.id,
            label: leadData.source.name
          },
          reason: leadData?.reason?.name,
          status: leadData?.status?.id,
          demo_date: leadData?.demo_date && displayDate(leadData?.demo_date),
          attachment: leadData?.attachment?.id,
          custom_field_values: undefined,
          contact: {
            ...leadData?.contact,
            leads: leadData?.contact?.leads?.map((lead) => lead?.id),
            custom_field_values: undefined
          }
        })
      }

      if (leadData?.custom_field_values && customFieldsLead?.results) {
        const customFieldFormData = transformCustomFieldValuesToForm(
          leadData?.custom_field_values
        )

        Object.assign(formData, { custom_field_values: customFieldFormData })
      }

      if (
        leadData?.contact?.custom_field_values &&
        customFieldsContact?.results
      ) {
        const customFieldFormData = transformCustomFieldValuesToForm(
          leadData?.contact.custom_field_values
        )

        Object.assign(formData, {
          contact: {
            ...formData.contact,
            custom_field_values: customFieldFormData
          }
        })
      }

      form.setFieldsValue(formData)
    }
  }, [
    user,
    form,
    leadData,
    customFieldsLead,
    customFieldsContact,
    leadsTypesData,
    lead
  ])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [currentStep])

  const title = id ? t('lead.edit_lead') : t('lead.add_lead')

  return (
    <Flex vertical gap={16} align="center" justify="center" className="mb-24">
      <Steps
        current={currentStep}
        progressDot
        items={currentComponent.map(({ title }) => ({ title }))}
        className={styles.leadSteps}
      />

      <Card
        className={styles.leadCard}
        title={leadData ? null : title}
        loading={isLoading}
        actions={[
          <Flex justify="end" key="actions" gap={12}>
            <Button disabled={isPending} onClick={onCancel}>
              {t(isFirstPage ? 'buttons.cancel' : 'buttons.prev')}
            </Button>

            <Button
              type="primary"
              disabled={isPending}
              loading={isPending}
              onClick={() => (isLastPage ? onSubmit() : nextStep())}
            >
              {t(isLastPage ? 'buttons.save' : 'buttons.next')}
            </Button>
          </Flex>
        ]}
      >
        <Form form={form} layout="vertical">
          {currentComponent[currentStep].child}
        </Form>
      </Card>
    </Flex>
  )
}
